import * as React from 'react'
import { FC, useState } from 'react'
import { useStaticQuery, graphql, Link } from 'gatsby'
import { useMediaQuery } from 'react-responsive'
import InfiniteScroll from 'react-infinite-scroll-component'
import MainSection, { MainSectionParts } from '../../components/MainSection/MainSection'
import Layout from '../../components/Layout/Layout'
import Image from '../../components/Image/Image'
import { ROUTE_PATH, SITE_URL } from '../../utils/constants'
import { cls } from '../../utils/utils'
import * as globalStyle from '../../styles/constants.scss'
import Tags from '../../components/Tags/Tags'
import SimpleMarkdown from '../../components/SimpleMarkdown/SimpleMarkdown'
import { IPage, IPressProps, IPressTemplateProps, IPress, IPressItemProps } from './Press.types'
import * as styles from './Press.module.scss'

export const pressQuery = graphql`
  query {
    markdownRemark(frontmatter: { templateKey: { eq: "Press" } }) {
      frontmatter {
        title
        backgroundImage {
          publicURL
          childImageSharp {
            fluid(maxWidth: 2000) {
              ...GatsbyImageSharpFluid
            }
          }
        }
        newsTitle
        postsTitle
      }
    }
  }
`

const Press: FC<IPressProps> = ({ pageContext }) => {
  const data = useStaticQuery(pressQuery)
  return (
    <Layout title={ROUTE_PATH.press.name} showScrollToTop>
      <PressTemplate {...data.markdownRemark.frontmatter} pageContext={pageContext} />
    </Layout>
  )
}

/**
 * The commented out code causes the following behaviour:
 * if some pages of infinite scroll are being loading
 * and a user starts scrolling immediately after returning back from /pages/(news|posts)/...
 * then the page will jump to a restored scroll position.
 * It's worth noticing, Gatsby restores scroll position as well, but it doesn't wait until all data will be loaded.
 * So it restores scroll only inside the first page's range.
 */
// let previousScrollY = 0
// const saveScroll = () => (previousScrollY = window.scrollY)
//
// const usePreviousScrollPosition = () => {
//   useEffect(() => {
//     if (
//       /^\/pages\/(news|posts)\//i.test(sessionStorage.getItem('prevPath') ?? '') &&
//       previousScrollY <= document.documentElement.scrollHeight
//     ) {
//       window.scrollTo({ top: previousScrollY })
//     }
//   })
// }

export const useMobileLayout = () => typeof window !== 'undefined' && useMediaQuery({ maxWidth: globalStyle.bpLarge })

export const getTabParam = () => {
  if (typeof window !== 'undefined') {
    const search = window.location.search
    const params = new URLSearchParams(search)
    return params.get('tab')
  }
  return null
}

export const setTabParam = (tab: string, searchQuery?: string) => {
  if (typeof window !== 'undefined') {
    window.history.replaceState(null, '', `?tab=${tab}${searchQuery ? `&search=${searchQuery}` : ''}`)
  }
}

export const PressTemplate: FC<IPressTemplateProps> = ({ title, backgroundImage, newsTitle, postsTitle, pageContext }) => {
  let searchParams
  if (typeof window !== 'undefined') {
    const { search } = window && window.location
    searchParams = new URLSearchParams(search).get('search')
  }
  const [searchQuery, setSearchQuery] = useState(searchParams || '')
  // usePreviousScrollPosition()

  const newsTab = newsTitle?.toLowerCase().split(' ').join('-')
  const postsTab = postsTitle?.toLowerCase().split(' ').join('-')

  const [news, setNews] = useState({
    nextPage: 1,
    hasMore: pageContext?.hasMoreNews ?? false,
    content: pageContext?.news ?? [],
  })
  const loadMoreNews = () => loadMorePress('news', news, setNews)
  const newsList = createInfiniteScroll(news.content, loadMoreNews, news.hasMore, News)

  const [posts, setPosts] = useState({
    nextPage: 1,
    hasMore: pageContext?.hasMorePosts ?? false,
    content: pageContext?.posts ?? [],
  })
  const loadMorePosts = () => loadMorePress('posts', posts, setPosts)
  const postsList = createInfiniteScroll(posts.content, loadMorePosts, posts.hasMore, Post)

  const isMobileLayout = useMobileLayout()
  const isDesktopLayout = !isMobileLayout

  const initialTab = getTabParam() ?? newsTab
  if (isMobileLayout) {
    setTabParam(initialTab)
  }
  const [activeTab, setActiveTab] = useState(initialTab)
  const switchTo = (tab: string) => () => {
    // saveScroll()
    setTabParam(tab)
    setActiveTab(tab)
  }
  const activeWhen = (tab: string) => (activeTab === tab ? styles.active : styles.inactive)
  const Tabs: FC = () => (
    <div className={styles.tabs}>
      <div className={cls(styles.tab, activeWhen(newsTab))} onClick={switchTo(newsTab)}>
        <h3 className={styles.tabTitle}>{newsTitle}</h3>
      </div>
      <div className={cls(styles.tab, activeWhen(postsTab))} onClick={switchTo(postsTab)}>
        <h3 className={styles.tabTitle}>{postsTitle}</h3>
      </div>
    </div>
  )

  return (
    <>
      <MainSection>
        <MainSectionParts.FirstSection image={backgroundImage} shifted>
          <h1 className={styles.mainSectionHeader}>{title}</h1>
        </MainSectionParts.FirstSection>

        {isMobileLayout && <Tabs />}

        <MainSectionParts.SecondSection
          showTags
          dontShowParallelepiped
          dontShowRectangles
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
        >
          <div className={cls('grid-12', styles.darkColor)}>
            {isDesktopLayout && (
              <>
                <h1 className={cls(styles.sectionHeader, styles.newsSectionHeader)}>{newsTitle}</h1>
                <h1 className={cls(styles.sectionHeader, styles.blogSectionHeader)}>{postsTitle}</h1>
              </>
            )}
            {(isDesktopLayout || activeTab === newsTab) && <section className={styles.newsContainer}>{newsList}</section>}
            {(isDesktopLayout || activeTab === postsTab) && <section className={styles.postsContainer}>{postsList}</section>}
          </div>
        </MainSectionParts.SecondSection>
      </MainSection>
    </>
  )
}

interface IPressState {
  nextPage: number
  hasMore: boolean
  content: IPress[]
}

const loadMorePress = (pressType: 'news' | 'posts', pressState: IPressState, setPressState: (nextState: IPressState) => void) => {
  const nextPageUrl = `${SITE_URL}jsonPages/${pressType}/${pressState.nextPage}.json`
  fetch(nextPageUrl)
    .then((result) => result.json())
    .then(({ hasMore, content }: IPage) => {
      setPressState({
        hasMore,
        nextPage: pressState.nextPage + 1,
        content: pressState.content.concat(content),
      })
    })
    .catch(console.error)
}

const createInfiniteScroll = (items: IPress[], next: () => void, hasMore: boolean, PressComponent: FC<IPressItemProps>) => (
  <InfiniteScroll
    style={{ overflow: 'unset' }}
    dataLength={items.length}
    next={next}
    hasMore={hasMore}
    loader={<h3>Loading...</h3>}
    scrollThreshold={0.5}
  >
    {items.map(({ id, fields, frontmatter }) => (
      <React.Fragment key={id}>
        <PressComponent {...fields} {...frontmatter} />
      </React.Fragment>
    ))}
  </InfiniteScroll>
)

const News: FC<IPressItemProps> = ({ slug, previewImage, title, date, tags, summary }) => {
  const image = previewImage && <Image className={styles.image} fixed image={previewImage} />

  return (
    <Link to={slug} className={styles.link}>
      <div className={cls(styles.news, styles.scalable)}>
        {/* <Link to={slug} className={styles.link} onClick={saveScroll}> */}
        <div>{image}</div>

        <small className={cls('text--caption', styles.date)}>{date}</small>
        <h3 className={styles.newsTitle}>{title}</h3>
        {summary && <SimpleMarkdown body={summary} className={cls(styles.summary)} />}
        {tags && <Tags tags={tags} />}
      </div>
    </Link>
  )
}

const Post: FC<IPressItemProps> = ({ slug, previewImage, title, date, tags, summary }) => {
  const image = previewImage && <Image className={styles.image} fixed image={previewImage} />

  return (
    <div className={styles.post}>
      {/* <Link to={slug} className={cls(styles.link, 'text--large')} onClick={saveScroll}> */}
      <Link to={slug} className={cls(styles.link, styles.colorable)}>
        <div>{image}</div>
        <small className={cls('text--caption', styles.date)}>{date}</small>
        <h3 className={styles.newsTitle}>{title}</h3>
        {summary && <SimpleMarkdown body={summary} className={cls(styles.summary)} />}
      </Link>
      {tags && <Tags tags={tags} />}
    </div>
  )
}

export default Press
